import {
    render,
    updateOffset,
    updateFocus,
    updateEnlarge,
    updateResolution,
    updateDilation,
    initRenderStore,
    updateTexture,
} from './FJFake3dRenderStore';
import FJUtil from '../util/FJUtil';
import { ANIMATION_STYLE_LIST } from '../config/dataConfig/FJFake3dData';
import FJFake3dExportStore from './FJFake3dExportStore';

let canvas = null;
let timer = null;
let _canvasSize = { width: 0, height: 0 };
let _viewSize = { width: 0, height: 0 };
let _imageSize = { width: 0, height: 0 };
let _currentOriginalImageUrl = null;
let _currentDepthMapImageUrl = null;
let _startPoint = {
    x: 0.05,
    y: 0,
    z: 0,
};
let _middlePoint = {
    x: 0,
    y: 0,
    z: 0,
};
let _endPoint = {
    x: -0.05,
    y: 0,
    z: 0,
};
let _amplitudePoint = {
    x: 0.1,
    y: 0.1,
    z: 0,
};
let _phasePoint = {
    x: 0,
    y: 0.25,
    z: 0.25,
};
let _amountOfMotion = 1;
let _animationDuration = 4;
let _focus = 0.5;
let _edgeDilation = 0.002;
let _motionType = 'LINEAR';
let _isLoop = true;
let _isReverse = false;
let _intensity = 50;
let _animationType = 'vertical';
let _directionType = 'vertical';
function bindCanvas(renderCanvas) {
    if (renderCanvas === canvas) {
        return;
    }
    canvas = renderCanvas;
}

function updateAnimationRender(canvasSize = _canvasSize, options) {
    if (!canvasSize || !canvas) {
        return;
    }
    const {
        startPoint,
        middlePoint,
        endPoint,
        animationDuration,
        amountOfMotion,
        enlarge,
        focus,
        edgeDilation,
        amplitudePoint,
        phasePoint,
        motionType,
        isLoop,
        isReverse,
    } = options;

    updateEnlarge(enlarge);
    updateFocus(focus);
    updateDilation(edgeDilation);

    if (timer) {
        cancelAnimationFrame(timer);
        timer = null;
    }

    const isCircular = motionType === 'CIRCULAR';

    const tickRender = () => {
        tick(canvas, {
            canvasSize,
            timestamp: performance.now() / 1e3,
            motionType,
            animationDuration,
            startPoint,
            middlePoint,
            endPoint,
            isCircular,
            isLoop,
            isReverse,
            amplitudePoint,
            phasePoint,
            amountOfMotion,
        });

        timer = requestAnimationFrame(tickRender);
    };
    timer = requestAnimationFrame(tickRender);
}

function tick(canvas, options) {
    const {
        canvasSize,
        timestamp,
        motionType,
        startPoint,
        middlePoint,
        endPoint,
        animationDuration,
        isCircular,
        isLoop,
        isReverse,
        amplitudePoint,
        phasePoint,
        amountOfMotion,
    } = options;

    const progress = calcProgress(timestamp / animationDuration, isCircular, isLoop, isReverse);
    const offset = { x: 0, y: 0, z: 0 };
    if (motionType === 'CIRCULAR') {
        const [x, y, z] = calcCircularProgressOffset(
            [amplitudePoint.x, amplitudePoint.y, amplitudePoint.z],
            [phasePoint.x, phasePoint.y, phasePoint.z],
            amountOfMotion,
            progress,
        );
        offset.x = x;
        offset.y = y;
        offset.z = z;
    } else {
        const middlePointArr = motionType === 'LINEAR' ? null : [middlePoint.x, middlePoint.y, middlePoint.z];
        const [x, y, z] = calcProgressOffset(
            [startPoint.x, startPoint.y, startPoint.z],
            middlePointArr,
            [endPoint.x, endPoint.y, endPoint.z],
            amountOfMotion,
            progress,
        );
        offset.x = x;
        offset.y = y;
        offset.z = z;
    }

    updateOffset(offset);

    if (canvas) {
        canvas.width = canvasSize.width;
        canvas.height = canvasSize.height;
        render(canvas);
    }
}

function calcProgress(value, isCircular, loop, reverse) {
    let result = isCircular !== loop ? 1 - Math.abs((value % 2) - 1) : value % 1;
    if (reverse) {
        result = 1 - result;
    }
    return result;
}

function calcProgressValue(startValue, middleValue, endValue, progress) {
    const coefficientY =
        (1 * (middleValue - startValue) + 0.5 * (startValue - endValue) + 0 * (endValue - middleValue)) / -0.25;
    const coefficientC =
        (1 * 1 * (startValue - middleValue) + 0.5 * 0.5 * (endValue - startValue) + 0 * 0 * (middleValue - endValue)) /
        -0.25;
    const coefficientO =
        (0.5 * 1 * (0.5 - 1) * startValue + 1 * 0 * 1 * middleValue + 0 * 0.5 * (0 - 0.5) * endValue) / -0.25;
    return coefficientY * progress * progress + coefficientC * progress + coefficientO;
}

function calcCircularProgressOffset(amplitudePoint, phasePoint, amountOfMotion, progress) {
    return [
        Math.sin(2 * Math.PI * (progress + phasePoint[0])) * amplitudePoint[0] * amountOfMotion,
        Math.sin(2 * Math.PI * (progress + phasePoint[1])) * amplitudePoint[1] * amountOfMotion,
        0.5 * (1 + Math.sin(2 * Math.PI * (progress + phasePoint[2]))) * amplitudePoint[2] * amountOfMotion,
    ];
}

function calcProgressOffset(startPoint, middlePoint, endPoint, amountOfMotion, progress) {
    if (middlePoint === null) {
        middlePoint = endPoint.map((value, index) => (value - startPoint[index]) / 2 + startPoint[index]);
    }
    startPoint = startPoint.map(value => value * amountOfMotion);
    const scaledMiddlePoint = middlePoint.map(value => value * amountOfMotion);
    endPoint = endPoint.map(value => value * amountOfMotion);
    return startPoint.map((value, index) =>
        calcProgressValue(value, scaledMiddlePoint[index], endPoint[index], progress),
    );
}

function init() {
    initRenderStore();
    FJFake3dExportStore.loadEncoderWorker();
}
async function setImages(originalImageUrl, depthMapImageUrl) {
    if (_currentOriginalImageUrl === originalImageUrl && _currentDepthMapImageUrl === depthMapImageUrl) return;

    const originalImage = await FJUtil.loadImageAsync(originalImageUrl);
    const depthMapImage = await FJUtil.loadImageAsync(depthMapImageUrl);
    if (originalImage.width !== depthMapImage.width || originalImage.height !== depthMapImage.height) {
        const canvas = document.createElement('canvas');
        canvas.width = originalImage.width;
        canvas.height = originalImage.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(depthMapImage, 0, 0, originalImage.width, originalImage.height);
            const resizedDepthMapImageUrl = canvas.toDataURL();
            const resizedDepthMapImage = await FJUtil.loadImageAsync(resizedDepthMapImageUrl);
            updateTexture(originalImage, resizedDepthMapImage);
        }
    } else {
        updateTexture(originalImage, depthMapImage);
    }
    const imageSize = { width: originalImage.width, height: originalImage.height };
    _imageSize = imageSize;

    if (_viewSize.width && _viewSize.height) {
        updateCanvasSizeAndRender(imageSize, _viewSize);
    }
}
function updateCanvasSizeAndRender(imageSize, viewSize) {
    const canvasSize = FJUtil.fitSize(imageSize, viewSize);
    _canvasSize = canvasSize;
    updateResolution(canvasSize.width, canvasSize.height);
    triggerAnimationRender();
}
function triggerAnimationRender() {
    const animationData = getAnimationData();
    updateAnimationRender(_canvasSize, animationData);
}
function getAnimationData() {
    return {
        startPoint: _startPoint,
        middlePoint: _middlePoint,
        endPoint: _endPoint,
        amplitudePoint: _amplitudePoint,
        phasePoint: _phasePoint,
        amountOfMotion: _amountOfMotion,
        animationDuration: _animationDuration,
        enlarge: 1 + calculateCropValue(),
        focus: _focus,
        edgeDilation: _edgeDilation,
        motionType: _motionType,
        isLoop: _isLoop,
        isReverse: _isReverse,
    };
}
function updateViewSize(width, height) {
    const viewSize = { width, height };
    _viewSize = viewSize;
    if (_imageSize.width && _imageSize.height) {
        updateCanvasSizeAndRender(_imageSize, viewSize);
    }
}
function calculateCropValue() {
    let motionType = _motionType;
    let startPoint = _startPoint;
    let endPoint = _endPoint;
    let middlePoint = _middlePoint;
    let amplitudePoint = _amplitudePoint;
    let amountOfMotion = _amountOfMotion;
    let focus = _focus;
    const calculateMaxValue = (points, focus, amountOfMotion) =>
        Math.max(
            ...[-0.5, 0.5].map(offset => {
                const adjustedPoints1 = points
                    .map(
                        value =>
                            (1 - amountOfMotion * focus) * offset + amountOfMotion * value + offset * focus - value,
                    )
                    .map(Math.abs);
                const adjustedPoints2 = points
                    .map(value => (1 - amountOfMotion * focus) * offset + amountOfMotion * value)
                    .map(Math.abs);
                return Math.max(Math.max(...adjustedPoints1), Math.max(...adjustedPoints2));
            }),
        );

    const type = motionType;
    if (type === 'CIRCULAR') {
        const amplitudeX = amplitudePoint.x;
        const amplitudeY = amplitudePoint.y;
        const amplitudeZ = amplitudePoint.z;
        const maxValue = calculateMaxValue(
            [amplitudeX * amountOfMotion, amplitudeY * amountOfMotion].map(Math.abs),
            amplitudeZ * amountOfMotion,
            focus,
        );
        return ((Math.max(maxValue, 0.5) - 0.5) / (2 * maxValue)) * 2;
    } else {
        const startPosition = [startPoint.x, startPoint.y, startPoint.z].map(value => value * amountOfMotion);
        const middlePosition =
            type === 'THREEPOINTLINEAR'
                ? [middlePoint.x, middlePoint.y, middlePoint.z].map(value => value * amountOfMotion)
                : [0, 0, 0];
        const endPosition = [endPoint.x, endPoint.y, endPoint.z].map(value => value * amountOfMotion);
        const maxStartValue = calculateMaxValue(
            [startPosition[0], startPosition[1]].map(Math.abs),
            startPosition[2],
            focus,
        );
        const maxMiddleValue =
            type === 'THREEPOINTLINEAR'
                ? calculateMaxValue([middlePosition[0], middlePosition[1]].map(Math.abs), middlePosition[2], focus)
                : 0;
        const maxEndValue = calculateMaxValue([endPosition[0], endPosition[1]].map(Math.abs), endPosition[2], focus);
        const maxValue = Math.max(Math.max(maxStartValue, maxMiddleValue), maxEndValue);
        return ((Math.max(maxValue, 0.5) - 0.5) / (2 * maxValue)) * 2;
    }
}
const calcCoord = (percent, minValue, maxValue, defaultValue) => {
    const { x: minX, y: minY, z: minZ } = minValue;
    const { x: maxX, y: maxY, z: maxZ } = maxValue;
    const { x: defaultX, y: defaultY, z: defaultZ } = defaultValue;
    if (percent > 0.5) {
        const pct = (percent - 0.5) / 0.5;
        return {
            x: calcPercentValue(pct, defaultX, maxX),
            y: calcPercentValue(pct, defaultY, maxY),
            z: calcPercentValue(pct, defaultZ, maxZ),
        };
    } else if (percent < 0.5) {
        const pct = percent / 0.5;
        return {
            x: calcPercentValue(pct, minX, defaultX),
            y: calcPercentValue(pct, minY, defaultY),
            z: calcPercentValue(pct, minZ, defaultZ),
        };
    } else {
        return defaultValue;
    }
};
const calcPercentValue = (percent, min, max) => {
    return min + (max - min) * percent;
};
function updateIntensityInfo() {
    const intensity = _intensity;
    const animationType = _animationType;
    const directionType = _directionType;
    const animationList = ANIMATION_STYLE_LIST.find(item => item.type === animationType);
    const presetData =
        (animationList && animationList.directions.find(item => item.type === directionType)) ||
        animationList.directions[0];
    const motionType = presetData.defaultValue.motionType;
    const percent = intensity / 100;
    const maxValue = presetData?.maxValue;
    const minValue = presetData?.minValue;
    const defaultValue = presetData?.defaultValue;
    _amountOfMotion = presetData.defaultValue.amountOfMotion;
    _focus = presetData.defaultValue.focus;
    _edgeDilation = presetData.defaultValue.edgeDilation;
    presetData.defaultValue.phasePoint && (_phasePoint = presetData.defaultValue.phasePoint);
    _motionType = motionType;

    switch (motionType) {
        case 'CIRCULAR':
            _amplitudePoint = calcCoord(
                percent,
                minValue.amplitudePoint,
                maxValue.amplitudePoint,
                defaultValue.amplitudePoint,
            );
            break;
        case 'LINEAR':
            _startPoint = calcCoord(percent, minValue.startPoint, maxValue.startPoint, defaultValue.startPoint);
            _endPoint = calcCoord(percent, minValue.endPoint, maxValue.endPoint, defaultValue.endPoint);
            break;
        case 'THREEPOINTLINEAR':
            _startPoint = calcCoord(percent, minValue.startPoint, maxValue.startPoint, defaultValue.startPoint);
            _endPoint = calcCoord(percent, minValue.endPoint, maxValue.endPoint, defaultValue.endPoint);
            _middlePoint = calcCoord(percent, minValue.middlePoint, maxValue.middlePoint, defaultValue.middlePoint);
            break;
        default:
            break;
    }
    // triggerAnimationRender();
}

function updateIntensity(value) {
    _intensity = value;
    updateIntensityInfo();
}
function updateAnimationType(value) {
    _animationType = value;
    updateIntensityInfo();
}
function updateDirectionType(value) {
    _directionType = value;
    updateIntensityInfo();
}
function updateAnimationDuration(duration) {
    _animationDuration = duration;
}
function updateLoop(bool) {
    _isLoop = bool;
}
function updateIsReverse(bool) {
    _isReverse = bool;
}
function getImageSize() {
    return _imageSize;
}
async function renderCanvas(url) {
    if (canvas) {
        const ctx = canvas.getContext('2d');
        const image = await FJUtil.loadImageAsync(url);
        ctx.drawImage(image, 0, 0, image.width, image.height);
    }
}
export {
    bindCanvas,
    updateAnimationRender,
    calcProgressOffset,
    tick,
    init,
    setImages,
    updateViewSize,
    updateIntensity,
    updateAnimationDuration,
    updateLoop,
    updateIsReverse,
    getAnimationData,
    getImageSize,
    triggerAnimationRender,
    updateAnimationType,
    updateDirectionType,
    renderCanvas,
};
